<script lang="ts">
import { usePracticas, useTerms } from '~/api/drupal'
import type { Term } from '~/api/drupal/useTerms'
export const usePracticasFilters = async () => {
  const { t } = useI18n()
  const { terms } = await useTerms(['cbz_education', 'cbz_region'])

  const allEducationIds = terms.value.cbz_education.map((item: Term) => item.id)
  const allRegionIds = terms.value.cbz_region.map((item: Term) => item.id)
  const allStudyModes = ['full_time', 'part_time']

  const filter = reactive({
    education: allEducationIds,
    region: allRegionIds,
    studyMode: [...allStudyModes],
  })

  const reset = () => {
    filter.education = allEducationIds
    filter.region = allRegionIds
    filter.studyMode = [...allStudyModes]
  }

  const options = computed(() => {
    return {
      education: terms.value.cbz_education.map((item: Term) => ({
        label: item.name,
        value: item.id,
      })),
      region: terms.value.cbz_region,
      studyMode: [
        { label: t('practicas.list.studyMode.fullTime'), value: 'full_time' },
        { label: t('practicas.list.studyMode.partTime'), value: 'part_time' },
      ],
    }
  })

  const educationChange = (data: (string | number)[]) => {
    if (!data.length) {
      filter.education = allEducationIds
    }
  }

  watch(
    () => filter.education,
    (val) => educationChange(val as (string | number)[]),
    { deep: true },
  )

  return {
    filter,
    options,
    reset,
  }
}
</script>

<script setup lang="ts">
import { useScrollToTargetOnChange } from '~/composables/useScrollToTargetOnChange'

useHead({
  bodyAttrs: {
    class: 'practicas-listing',
  },
})

const { t } = useI18n()

const { filter, options, reset } = await usePracticasFilters()
const { items, page, maxPages, downloadUrl, showStudyMode } = await usePracticas({ filter })

const { scrollTarget } = useScrollToTargetOnChange(page)
</script>

<template>
  <SectionFilters>
    <Checkbox
      id="practicas-listing-education"
      v-model="filter.education"
      :options="options.education"
      :required="false"
    />
    <div class="study-mode" :class="{ 'is-collapsed': !showStudyMode }">
      <div class="study-mode-inner" :inert="!showStudyMode">
        <h5 class="study-mode-label label">{{ t('practicas.list.studyMode.label') }}</h5>
        <Checkbox
          id="practicas-listing-study-mode"
          v-model="filter.studyMode"
          :options="options.studyMode"
          :required="false"
        />
      </div>
    </div>
    <div class="regions">
      <Tags v-model:active="filter.region" :tags="options.region" :all="true" />
    </div>
    <ul class="actions cul f-18">
      <li>
        <a v-if="items.length > 0" :href="downloadUrl" target="_blank" download>
          <Icon name="web" />
          {{ t('practicas.list.exportPdfLabel') }}
        </a>
      </li>
      <li>
        <button class="link-default" @click="reset">{{ t('practicas.list.resetFiltersLabel') }}</button>
      </li>
    </ul>
  </SectionFilters>
  <Section>
    <ul ref="scrollTarget" class="items cul tl-11 tl-o-1">
      <li v-for="(item, index) in items" :key="`${index}-${item?.id}`" class="item">
        <PracticaItem :item="item" />
      </li>
    </ul>
    <Pagination v-if="maxPages > 1" v-model:page="page" class="tl-11 tl-o-1" :max="maxPages" />
  </Section>
</template>
<style scoped lang="scss">
.section-filters {
  :deep(.checkbox-fieldset .formkit-options) {
    display: flex;
    gap: 24px;
    flex-direction: row;
    flex-wrap: wrap;
    margin-bottom: 24px;
  }
}
.study-mode {
  display: grid;
  grid-template-rows: 1fr;
  transition:
    grid-template-rows 0.3s ease,
    margin-bottom 0.3s ease;
}
.study-mode-inner {
  overflow: hidden;
  min-height: 0;
  transition: opacity 0.3s ease;
}
div.study-mode.is-collapsed {
  grid-template-rows: 0fr;
  margin-bottom: 0;
  .study-mode-inner {
    opacity: 0;
    pointer-events: none;
  }
}
.study-mode-label {
  color: var(--c-primary);
  margin-bottom: 12px;
}
.regions {
  @include breakpoint(tl down) {
    position: relative;
    width: calc(50% + 50vw);
    overflow-x: auto;
    overflow-y: hidden;
  }
}

.tags {
  @include breakpoint(tl down) {
    flex-wrap: nowrap;
  }

  :deep(li) {
    flex-shrink: 0;
  }
}
.actions {
  color: var(--c-primary);
  display: flex;
  gap: 24px;
  justify-content: flex-end;
  align-items: center;
  a {
    display: flex;
    gap: 6px;
    align-items: center;
    text-decoration: none;
  }
}

.items {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px 8px;

  @include breakpoint(ts) {
    grid-template-columns: repeat(2, 1fr);
  }
  @include breakpoint(ds) {
    grid-template-columns: repeat(3, 1fr);
  }
}
.pagination {
  margin-top: 48px;
}
</style>
